import React, { useEffect, useState } from 'react';
import { FaSearch, FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import { Modal, Button, Form } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';

function BtsVideoList() {
    const [videos, setVideos] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(100);

    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [videoId, setVideoId] = useState(null);
    const [videoFile, setVideoFile] = useState(null);
    const [videoPreview, setVideoPreview] = useState(null);

    const fetchVideos = async () => {
        const token = localStorage.getItem('authToken');
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/getbts`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const result = await res.json();
            setVideos(result.videos || []);  // <-- use result.videos not result.data
        } catch (error) {
            console.error('Error fetching videos:', error);
        }
    };

    useEffect(() => {
        fetchVideos();
    }, []);

    const filteredVideos = videos.filter(video =>
        Object.values(video).some(value =>
            String(value).toLowerCase().includes(searchTerm.toLowerCase())
        )
    );

    const indexOfLastItem = currentPage * itemsPerPage;
    const currentVideos = filteredVideos.slice(indexOfLastItem - itemsPerPage, indexOfLastItem);
    const totalPages = Math.ceil(filteredVideos.length / itemsPerPage);

    const handlePageChange = (page) => setCurrentPage(page);

    const handleAddOrEditVideo = async () => {
        const token = localStorage.getItem('authToken');
        const formData = new FormData();
        formData.append('bts_video_name', videoFile.name);
        formData.append('bts_video', videoFile);

        const url = isEditing
            ? `${process.env.REACT_APP_API_URL}/videos/${videoId}`
            : `${process.env.REACT_APP_API_URL}/addbts`;

        const method = isEditing ? 'PUT' : 'POST';

        try {
            const res = await fetch(url, {
                method,
                headers: { Authorization: `Bearer ${token}` },
                body: formData,
            });
            if (!res.ok) throw new Error('Failed to upload video');
            fetchVideos();
            resetForm();
            setShowModal(false);
        } catch (err) {
            console.error(err);
        }
    };

    const handleEditClick = (video) => {
        setVideoId(video.bts_video_id);
        setIsEditing(true);
        setVideoPreview(video.bts_video_link);
        setShowModal(true);
    };

    const handleDeleteClick = (id) => {
        setVideoId(id);
        setShowDeleteModal(true);
    };

    const handleDeleteVideo = async () => {
        const token = localStorage.getItem('authToken');
        try {
            await fetch(`${process.env.REACT_APP_API_URL}/deletebts/${videoId}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` },
            });
            fetchVideos();
            setShowDeleteModal(false);
        } catch (err) {
            console.error('Error deleting video:', err);
        }
    };

    const handleVideoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setVideoFile(file);
            setVideoPreview(URL.createObjectURL(file));
        }
    };

    const resetForm = () => {
        setVideoId(null);
        setIsEditing(false);
        setVideoFile(null);
        setVideoPreview(null);
    };

    return (
        <div className="container" style={{ marginTop: '80px' }}>
            <h2 className="text-primary">BTS Video List</h2>

            <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="input-group me-3" style={{ maxWidth: '300px' }}>
                    <span className="input-group-text bg-primary text-white">
                        <FaSearch />
                    </span>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search videos..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <button className="btn btn-success" onClick={() => setShowModal(true)}>
                    <FaPlus /> Add Video
                </button>
            </div>

            <table className="table table-bordered table-striped">
                <thead className="table-primary">
                    <tr>
                        <th>ID</th>
                        <th>Name</th>
                        <th>Link</th>    {/* Video preview */}
                        <th>Created At</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {currentVideos.map((video) => (
                        <tr key={video.bts_video_id}>
                            <td>{video.bts_video_id}</td>
                            <td>{video.bts_video_name}</td>
                            <td>{video.bts_video_link}</td>
                            <td>{new Date(video.createdAt).toLocaleDateString()}</td>
                            <td>
                                <button className="btn btn-sm btn-outline-success me-2" onClick={() => handleEditClick(video)}>
                                    <FaEdit /> Edit
                                </button>
                                <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteClick(video.bts_video_id)}>
                                    <FaTrash /> Delete
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <nav>
                <ul className="pagination justify-content-center">
                    {[...Array(totalPages)].map((_, i) => (
                        <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                            <button className="page-link" onClick={() => handlePageChange(i + 1)}>
                                {i + 1}
                            </button>
                        </li>
                    ))}
                </ul>
            </nav>

            <Modal show={showModal} onHide={() => { setShowModal(false); resetForm(); }}>
                <Modal.Header closeButton>
                    <Modal.Title>{isEditing ? 'Edit Video' : 'Add Video'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Upload Video</Form.Label>
                            <Form.Control type="file" accept="video/*" onChange={handleVideoChange} />
                            {videoPreview && (
                                <video src={videoPreview} controls style={{ width: '100%', marginTop: '10px' }} />
                            )}
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => { setShowModal(false); resetForm(); }}>
                        Close
                    </Button>
                    <Button variant="primary" onClick={handleAddOrEditVideo}>
                        {isEditing ? 'Update' : 'Add'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Deletion</Modal.Title>
                </Modal.Header>
                <Modal.Body>Are you sure you want to delete this video?</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
                    <Button variant="danger" onClick={handleDeleteVideo}>Delete</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default BtsVideoList;
